import React from "react";
import Link from "next/link";
import {
  ContactUsBlock,
  ContentTitles,
  ParallaxHero,
  RegularHero,
  ServiceBlock,
} from "@modes0/ui-components";
import {
  IconTiles,
  ServiceTiles,
  ServicesHighlights,
} from "@modes0/ui-components/client";
import { BUSINESS_NAME, businessDetails, contactDetails } from "@/content";
import { categoryDetails as category1 } from "@/content/services/1";
import { categoryDetails as category2 } from "@/content/services/2";
import {
  GENERAL_SERVICE_TERM_1,
  GENERAL_SERVICE_TERM_2,
  GENERAL_SERVICE_TERM_3,
  serviceBlockData,
} from "@/content/services/stand-alone-services";
import {
  parallaxContent1,
  parallaxContent2,
} from "@/content/parallax-heros/parallax-heros.content";
import { whyUs } from "@/content/why-us/why-us";
import type { CategoryDetails, SubCategory } from "@/types";
import GoogleContactFormWidget from "@/widgets/contact-form/GoogleContactFormWidget";
import {PageSeo} from '@modes0/ui-components-seo';
import {seoSiteProfile} from '@/content/seo';

const BRAND_BLUE = "#28aae1";
const BRAND_NAVY = "#15171e";
const BRAND_ORANGE = "#f68e22";

const getCategoryLink = (category: CategoryDetails) =>
  `/${category.categoryType}/${category.slug}`;
const getServiceLink = (category: CategoryDetails, service: SubCategory) =>
  service.link || `${getCategoryLink(category)}/${service.slug}`;
const withServiceLinks = (category: CategoryDetails): CategoryDetails => ({
  ...category,
  subCategories: category.subCategories?.map((service) => ({
    ...service,
    link: getServiceLink(category, service),
  })),
});

const serviceTilesDefaults = {
  Link: Link as never,
  variant: "overlay" as const,
  tileHeight: "160px",
  accentColor: BRAND_BLUE,
  backgroundColor: "#ffffff",
  panelBackgroundColor: "#f5f8fb",
  brandedOverlay: false,
  maxWidth: "1400px",
};

export default function Home() {
  const category1WithLinks = withServiceLinks(category1);
  const category2WithLinks = withServiceLinks(category2);
  const parallaxButtonLink1 =
    category1WithLinks.subCategories?.[0]?.link || getCategoryLink(category1);
  const parallaxButtonLink2 =
    category2WithLinks.subCategories?.[0]?.link || getCategoryLink(category2);

  return (
    <>
      <PageSeo siteProfile={seoSiteProfile} includeBusinessSchema />
      <div className="bg-white">
        <RegularHero
          Link={Link as never}
          image={{
            src: parallaxContent1.imageUrl,
            alt: `${BUSINESS_NAME} security services in Sydney`,
            decorative: false,
            position: "right top",
          }}
          eyebrow={parallaxContent1.overline}
          title={parallaxContent1.title}
          titleHighlight={null}
          titleHighlights={[]}
          titleComponent="h1"
          lead={parallaxContent1.subTitle}
          primaryAction={{
            label: parallaxContent1.buttonCTA,
            href: parallaxButtonLink1,
            ariaLabel: `${parallaxContent1.buttonCTA} with ${BUSINESS_NAME}`,
            icon: "arrowRight",
          }}
          secondaryAction={{
            label: `Contact ${BUSINESS_NAME}`,
            href: "/contact",
            ariaLabel: `Contact ${BUSINESS_NAME}`,
            variant: "outline",
            icon: false,
          }}
          trustItems={[
            {
              icon: "shield",
              title: "Systems and people",
              text: "CCTV, alarms, access control, guards and patrols scoped around the site",
            },
            {
              icon: "play",
              title: "Useful coverage",
              text: "Camera, alarm and access points planned around real routines and blind spots",
            },
            {
              icon: "sparkles",
              title: "Clear handover",
              text: "Practical setup, reporting and follow-up details for everyday use",
            },
          ]}
          accentColor={BRAND_BLUE}
          colors={{
            lightOverlay:
              "linear-gradient(90deg, #ffffff 0%, rgba(255,255,255,0.995) 34%, rgba(255,255,255,0.9) 48%, rgba(255,255,255,0.58) 64%, rgba(255,255,255,0.16) 82%, rgba(255,255,255,0.02) 100%)",
          }}
          backgroundColor="#ffffff"
          surfaceColor="#ffffff"
          textColor={BRAND_NAVY}
          textSoftColor="rgba(21, 23, 30, 0.72)"
          minHeight="clamp(520px, calc(100vh - 98px), 650px)"
          className="ozy-home-hero"
          ariaLabel={`${BUSINESS_NAME} security services in Sydney`}
        />

        <ServiceTiles
          {...serviceTilesDefaults}
          service={category1WithLinks as never}
          overline={BUSINESS_NAME}
          title={category1.name}
          text={category1.description?.[0] || category1.long_name}
          ctaLabel="View electronic security services"
          ctaHref={getCategoryLink(category1)}
          ctaAriaLabel={`View ${category1.name}`}
          listTitle="Electronic security services"
        />

        <ServicesHighlights
          category={category1WithLinks as never}
          alternate
          showMainCategoryLink
          accentColor={BRAND_BLUE}
          LinkComponent={Link}
        />

        <section className="bg-[#f5f8fb] px-4 py-10 md:py-14">
          <ContentTitles
            title={`Why ${BUSINESS_NAME}?`}
            subTitle={`Practical ${GENERAL_SERVICE_TERM_3} across Sydney`}
            subTitleComponent="h3"
            titleComponent="h2"
          />
          <div className="mx-auto max-w-7xl">
            <IconTiles
              content={whyUs.map(({ description, id, image, title }) => ({
                id,
                title,
                description,
                image: {
                  ...image,
                  url: image.url || image.original || "",
                  alt: image.alt || image.title,
                },
              }))}
              titleClasses="text-[20px] lg:text-[14px] text-center slide-down"
              subtitleClass="text-[14px] slide-down"
            />
          </div>
        </section>

        <ParallaxHero
          {...parallaxContent2}
          titleComponent="h2"
          buttonLink={parallaxButtonLink2}
          align="right"
        />

        <ServiceTiles
          {...serviceTilesDefaults}
          service={category2WithLinks as never}
          overline={BUSINESS_NAME}
          title={category2.name}
          text={category2.description?.[0] || category2.long_name}
          ctaLabel="View guarding and safety services"
          ctaHref={getCategoryLink(category2)}
          ctaAriaLabel={`View ${category2.name}`}
          backgroundColor="#f5f8fb"
          panelBackgroundColor="#ffffff"
          listTitle="Guarding and safety services"
        />

        <section className="bg-white px-4 py-10 md:py-14">
          <div className="mx-auto max-w-6xl">
            <ContactUsBlock
              contactDetails={contactDetails}
              title={`Contact ${BUSINESS_NAME}`}
              subtitle="Speak with Ozy Wide Group about CCTV, alarms, access control, guards, patrols, fire alarm support and security cabling across Sydney."
              showHeader
              className="ozy-contact-tiles"
              extraTileClasses="rounded-[8px]"
            />
          </div>
        </section>

        <ServicesHighlights
          category={category2WithLinks as never}
          alternateBackground
          alternate
          showMainCategoryLink
          accentColor={BRAND_ORANGE}
          LinkComponent={Link}
        />

        <section className="bg-[#edf7fc] py-10 md:py-14">
          <ContentTitles
            title="Security services for different site risks"
            subTitle={`Comprehensive ${GENERAL_SERVICE_TERM_2} and guarding support`}
            subTitleComponent="h3"
            titleComponent="h2"
          />
          <ServiceBlock
            data={serviceBlockData}
            className="ozy-service-block"
            maxWidth="1400px"
            pointsClasses=""
          />
          <div className="mt-6 text-center">
            <Link
              href="/contact"
              className="inline-flex min-h-11 items-center rounded-[8px] bg-[#28aae1] px-5 py-3 text-sm font-bold text-white no-underline transition hover:bg-[#15171e]"
            >
              Contact {BUSINESS_NAME}
            </Link>
          </div>
        </section>

        <section className="bg-white px-4 py-10 md:py-14">
          <ContentTitles
            title={category1.name}
            subTitle={`${businessDetails.generalServiceArea} ${GENERAL_SERVICE_TERM_1} systems`}
            subTitleComponent="h3"
            titleComponent="h2"
          />
          <ServiceTiles
            {...serviceTilesDefaults}
            service={category1WithLinks as never}
            ctaLabel="View all electronic security"
            ctaHref={getCategoryLink(category1)}
            ctaAriaLabel={`View ${category1.name}`}
          />
        </section>

        <section className="bg-[#f5f8fb] px-4 py-10 md:py-14">
          <ContentTitles
            title={category2.name}
            subTitle={`${businessDetails.generalServiceArea} guarding, patrol and safety support`}
            subTitleComponent="h3"
            titleComponent="h2"
          />
          <ServiceTiles
            {...serviceTilesDefaults}
            service={category2WithLinks as never}
            backgroundColor="#f5f8fb"
            panelBackgroundColor="#ffffff"
            ctaLabel="View all guarding services"
            ctaHref={getCategoryLink(category2)}
            ctaAriaLabel={`View ${category2.name}`}
          />
        </section>

        <GoogleContactFormWidget className="bg-white px-4 py-12 md:py-16" />
      </div>
    </>
  );
}
